import {dataFetcher} from "./dataGuetter/dataFetcher";
import {version_timed} from "../version";

/**
 * Remonter les plantages du front vers healthz.
 *
 * Une frontiere React affiche un ecran et l'erreur meurt dans la console de qui
 * l'a rencontree. Personne ne sait ce qui casse, ni ou, ni combien de fois.
 *
 * ## Trois precautions, et elles ne sont pas decoratives
 *
 * 1. On n'envoie qu'une fois la meme erreur par chargement de page. Un plantage
 *    dans une boucle de rendu se produit des dizaines de fois par seconde : sans
 *    ce garde-fou, la remontee ferait plus de degats que le bug.
 *
 * 2. Un plafond global par chargement. Le garde-fou precedent ne protege pas
 *    d'une erreur dont le message change a chaque fois — un identifiant, une
 *    date. Le plafond, si.
 *
 * 3. L'envoi ne peut jamais lever. On appelle ce code au pire moment : quelque
 *    chose vient de casser. Une remontee qui echoue bruyamment transformerait un
 *    bug en boucle.
 */

const CAP_PAR_CHARGEMENT = 20;

const dejaEnvoyees = new Set<string>();
let nbEnvoyees = 0;

export type NiveauSignalement = "bloc" | "page" | "application" | "fenetre" | "promesse";

interface Signalement {
    niveau:NiveauSignalement;
    error?:Error|null;
    /** Le nom du bloc, quand la frontiere en avait un a citer. */
    bloc?:string;
    /** La pile des composants React, que seule une frontiere possede. */
    componentStack?:string;
    /** Le code court affiche a l'utilisateur, pour le relier a sa capture d'ecran. */
    code?:string;
}

/**
 * Bruit connu, a ne pas remonter.
 *
 * « ResizeObserver loop limit exceeded » est emis par le navigateur lui-meme
 * quand un observateur declenche une mise en page pendant sa propre passe. Il
 * n'a aucune consequence visible et remplirait la table a lui seul.
 */
function estDuBruit(nom:string, message:string):boolean{
    if(message === "" ){
        return true;
    }
    return /ResizeObserver loop/i.test(message)
        || /Non-Error promise rejection captured/i.test(message)
        || nom === "AbortError";
}

export function signalerErreur({niveau, error, bloc, componentStack, code}:Signalement):void{
    try{
        const nom = error?.name || "Error";
        const message = error?.message || String(error || "");
        const route = window.location.pathname;

        if(estDuBruit(nom, message)){
            return;
        }

        // Meme empreinte que celle recalculee par l'API : le meme message sur
        // deux ecrans differents n'est pas le meme probleme.
        const empreinte = `${niveau}|${route}|${nom}|${message}`;
        if(dejaEnvoyees.has(empreinte) || nbEnvoyees >= CAP_PAR_CHARGEMENT){
            return;
        }
        dejaEnvoyees.add(empreinte);
        nbEnvoyees++;

        dataFetcher("front_errors", {
            method: "post",
            body: {
                niveau,
                route,
                bloc,
                code,
                errorName: nom,
                errorMessage: message,
                errorStack: error?.stack,
                componentStack,
                build: version_timed,
            },
        }).catch(()=>{
            // Voir la precaution 3 : on ne fait pas de bruit ici.
        });
    } catch (e){
        // Ni ici.
    }
}

/**
 * Ce que les frontieres ne verront jamais.
 *
 * Une frontiere React n'attrape que ce qui est lance pendant le rendu. Un
 * `onClick` qui plante, une promesse rejetee, un `setTimeout` qui echoue lui
 * passent a cote — et ce sont souvent ceux-la que l'utilisateur rencontre, parce
 * qu'ils surviennent quand il agit.
 *
 * Ces deux capteurs les recueillent. Ils ne remplacent pas les frontieres : ils
 * ne peuvent rien afficher, seulement raconter.
 */
export function installerCapteursGlobaux():void{
    window.addEventListener("error", (event:ErrorEvent)=>{
        signalerErreur({
            niveau: "fenetre",
            error: event.error instanceof Error ? event.error : new Error(event.message),
        });
    });

    window.addEventListener("unhandledrejection", (event:PromiseRejectionEvent)=>{
        const raison = event.reason;
        signalerErreur({
            niveau: "promesse",
            error: raison instanceof Error ? raison : new Error(String(raison)),
        });
    });
}
